import React from 'react';
import dayjs from 'dayjs';
import formBg from '../../assets/early-scrap-form.png';
import {
  EARLY_SCRAP_FORM_META,
  EARLY_SCRAP_PHOTO_BLUEPRINT,
  isEarlyScrapConstructionMachinery,
} from '../../constants/EarlyScrapConstants';
import {
  EarlyScrapConditionResult,
  EarlyScrapConfirmationMethod,
  EarlyScrapExteriorResult,
  EarlyScrapGlassResult,
  EarlyScrapHydraulicResult,
  EarlyScrapIdentityResult,
  EarlyScrapLightingResult,
  EarlyScrapOperationalJudgment,
  EarlyScrapOperationResult,
  EarlyScrapPowerTransmissionResult,
  EarlyScrapTireResult,
  EarlyScrapVehicleClass,
  EarlyScrapWorkingPartNoiseResult,
  EarlyScrapWorkingPartResult,
} from '../../enums/EarlyScrapEnums';

/** 서식 PNG 원본 해상도 2083×2946 — 좌표는 픽셀(항목 □/칸의 좌상단) */
const FW = 2083;
const FH = 2946;

/**
 * □ 크기 / 채우기 크기(px)
 * 시작점 = □ 좌상단 → 채우기는 □ 안쪽 가운데 (~90%)
 */
const CHECK_BOX = 17;
const CHECK_FILL = 15.5;
const CHECK_INSET = (CHECK_BOX - CHECK_FILL) / 2;

/** 칸 안 텍스트만 좌·상 여백 (시작점 = 칸 좌상단) */
const CELL_INSET_X = 5;
const CELL_INSET_Y = 4;

const styleXY = (x, y) => ({
  left: `${(x / FW) * 100}%`,
  top: `${(y / FH) * 100}%`,
});

/** 확인결과 □ 1열 / 2열 (사용자 측정 = □ 좌상단) */
const CB1 = 1440;
const CB2 = 1648;

const Mark = ({ x, y, on, blue }) =>
  on ? (
    <span
      className={`es-ov-mark${blue ? ' blue' : ''}`}
      style={styleXY(x + CHECK_INSET, y + CHECK_INSET)}
      aria-hidden
    />
  ) : null;

/** 칸 안 입력값 (성명, 주소, 판정 사유 등) — 여백 있음 */
const Field = ({ x, y, children, className = '' }) =>
  children ? (
    <span
      className={`es-ov-field ${className}`.trim()}
      style={styleXY(x + CELL_INSET_X, y + CELL_INSET_Y)}
    >
      {children}
    </span>
  ) : null;

/** 칸 밖·줄 위 텍스트 (년/월/일, 점검장) — 측정 좌표 그대로 */
const FieldPlain = ({ x, y, children, className = '' }) =>
  children ? (
    <span
      className={`es-ov-field es-ov-plain ${className}`.trim()}
      style={styleXY(x, y)}
    >
      {children}
    </span>
  ) : null;

const EarlyScrapPrintDocument = ({
  formValues = {},
  photoPreviews = {},
  associationSealUrl = '',
}) => {
  const owner = formValues.owner || {};
  const vehicle = formValues.vehicle || {};
  const r = formValues.inspectionResults || {};
  const judgment = formValues.judgment || {};
  const inspector = formValues.inspector || {};
  const method =
    formValues.confirmationMethod || EarlyScrapConfirmationMethod.ONSITE;
  const isOnline = method === EarlyScrapConfirmationMethod.ONLINE;
  const isOnsite = method === EarlyScrapConfirmationMethod.ONSITE;
  const isAuto =
    vehicle.vehicleClass !== EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY;
  const isHeavy = isEarlyScrapConstructionMachinery(vehicle.vehicleClass);

  const d = inspector.checkDate ? dayjs(inspector.checkDate) : dayjs();
  const y = d.isValid() ? d.format('YYYY') : '';
  const m = d.isValid() ? d.format('MM') : '';
  const day = d.isValid() ? d.format('DD') : '';
  const v = (x) => (x == null || x === '' ? '' : String(x));

  const glassVal = r.glass;
  const tireVal = r.tire;
  const lightingVal = r.lighting;
  const exteriorVal = r.exterior ?? r.exteriorAndBumper;
  const bumperVal = r.bumper;

  return (
    <div className="es-print">
      <section className="es-print-sheet">
        <div className="es-form-canvas">
          <img
            className="es-form-bg"
            src={formBg}
            alt={EARLY_SCRAP_FORM_META.title}
          />
          <div className="es-form-overlay">
            {/* 확인번호 */}
            <Field x={215} y={346} className="es-ov-num">
              {v(formValues.inspectNumber)}
            </Field>

            {/* 온라인 / 현장 */}
            <Mark x={1628} y={307} on={isOnline} />
            <Mark x={1628} y={350} on={isOnsite} />

            {/* 소유자 */}
            <Field x={798} y={390}>
              {v(owner.name)}
            </Field>
            <Field x={1407} y={390}>
              {v(owner.birthDate)}
            </Field>
            <Field x={798} y={466}>
              {v(owner.address)}
            </Field>
            <Field x={798} y={544}>
              {v(owner.phone)}
            </Field>
            <Field x={1407} y={544}>
              {v(owner.fax)}
            </Field>

            {/* 차량 */}
            <Field x={798} y={620}>
              {v(vehicle.licensePlateNumber)}
            </Field>
            <Field x={1407} y={620}>
              {v(vehicle.vin)}
            </Field>

            <Mark x={448} y={788} on={isAuto} />
            <Mark x={448} y={843} on={isHeavy} />

            {/* 연식·배기량·차량명·중량 — 값만 (양식 □는 체크하지 않음) */}
            <FieldPlain x={925} y={712} className="es-ov-sm">
              {v(vehicle.modelYear)}
            </FieldPlain>
            <FieldPlain x={1381} y={712} className="es-ov-sm">
              {v(vehicle.displacement)}
            </FieldPlain>
            <FieldPlain x={1167} y={772}>
              {v(vehicle.vehicleName)}
            </FieldPlain>
            <FieldPlain x={1006} y={843} className="es-ov-sm">
              {v(vehicle.seatingCapacity)}
            </FieldPlain>
            <FieldPlain x={1587} y={843}>
              {v(vehicle.heavyEquipmentSpec)}
            </FieldPlain>
            <FieldPlain x={1201} y={912} className="es-ov-sm">
              {v(vehicle.loadWeight)}
            </FieldPlain>
            <FieldPlain x={1478} y={912} className="es-ov-sm">
              {v(vehicle.totalWeight)}
            </FieldPlain>

            {/* 동일성 확인 */}
            <Mark
              x={1440}
              y={985}
              blue
              on={r.identity === EarlyScrapIdentityResult.MATCH}
            />
            <Mark
              x={1639}
              y={985}
              blue
              on={r.identity === EarlyScrapIdentityResult.MISMATCH}
            />
            <Mark
              x={1440}
              y={1026}
              blue
              on={r.identity === EarlyScrapIdentityResult.UNIDENTIFIABLE}
            />

            {/* 원동기 */}
            <Mark
              x={1440}
              y={1092}
              blue
              on={r.engine === EarlyScrapOperationResult.OK}
            />
            <Mark
              x={1648}
              y={1092}
              blue
              on={r.engine === EarlyScrapOperationResult.NG}
            />

            {/* 동력전달장치 */}
            <Mark
              x={1440}
              y={1154}
              blue
              on={
                r.powerTransmission === EarlyScrapPowerTransmissionResult.OK
              }
            />
            <Mark
              x={1649}
              y={1154}
              blue
              on={
                r.powerTransmission === EarlyScrapPowerTransmissionResult.NG
              }
            />
            <Mark
              x={1440}
              y={1193}
              blue
              on={
                r.powerTransmission ===
                EarlyScrapPowerTransmissionResult.FORWARD_REVERSE_NG
              }
            />

            {/* 조향 */}
            <Mark
              x={1440}
              y={1256}
              blue
              on={r.steering === EarlyScrapOperationResult.OK}
            />
            <Mark
              x={1648}
              y={1256}
              blue
              on={r.steering === EarlyScrapOperationResult.NG}
            />

            {/* 제동 */}
            <Mark
              x={1440}
              y={1316}
              blue
              on={r.brake === EarlyScrapOperationResult.OK}
            />
            <Mark
              x={1648}
              y={1316}
              blue
              on={r.brake === EarlyScrapOperationResult.NG}
            />

            {/* 유압 — 유압유 상태 (서식 PNG □ 좌상단 실측) */}
            <Mark
              x={CB1}
              y={1366}
              blue
              on={r.hydraulic === EarlyScrapHydraulicResult.GOOD}
            />
            <Mark
              x={CB2}
              y={1366}
              blue
              on={r.hydraulic === EarlyScrapHydraulicResult.BAD}
            />
            <Mark
              x={CB1}
              y={1404}
              blue
              on={r.hydraulic === EarlyScrapHydraulicResult.SIMPLE_BAD_OIL}
            />

            {/* 유압 — 작동상태 */}
            <Mark
              x={CB1}
              y={1449}
              blue
              on={
                r.workingPartOperation === EarlyScrapWorkingPartResult.GOOD
              }
            />
            <Mark
              x={CB2}
              y={1449}
              blue
              on={r.workingPartOperation === EarlyScrapWorkingPartResult.BAD}
            />
            <Mark
              x={CB1}
              y={1488}
              blue
              on={
                r.workingPartOperation ===
                EarlyScrapWorkingPartResult.SIMPLE_BAD
              }
            />

            {/* 유압 — 이상소음 */}
            <Mark
              x={CB1}
              y={1533}
              blue
              on={
                r.workingPartNoise === EarlyScrapWorkingPartNoiseResult.GOOD
              }
            />
            <Mark
              x={CB2}
              y={1533}
              blue
              on={r.workingPartNoise === EarlyScrapWorkingPartNoiseResult.BAD}
            />
            <Mark
              x={CB1}
              y={1571}
              blue
              on={
                r.workingPartNoise ===
                EarlyScrapWorkingPartNoiseResult.SIMPLE_BAD_NOISE
              }
            />

            {/* 차체 외관 */}
            <Mark
              x={CB1}
              y={1616}
              blue
              on={exteriorVal === EarlyScrapExteriorResult.GOOD}
            />
            <Mark
              x={CB2}
              y={1616}
              blue
              on={exteriorVal === EarlyScrapExteriorResult.BAD}
            />
            <Mark
              x={CB1}
              y={1655}
              blue
              on={exteriorVal === EarlyScrapExteriorResult.PARTIAL_DAMAGE}
            />

            {/* 완충장치 */}
            <Mark
              x={CB1}
              y={1701}
              blue
              on={bumperVal === EarlyScrapConditionResult.GOOD}
            />
            <Mark
              x={CB2}
              y={1701}
              blue
              on={bumperVal === EarlyScrapConditionResult.BAD}
            />

            {/* 유리창 */}
            <Mark
              x={CB1}
              y={1748}
              blue
              on={glassVal === EarlyScrapGlassResult.GOOD}
            />
            <Mark
              x={CB2}
              y={1748}
              blue
              on={glassVal === EarlyScrapGlassResult.BAD}
            />
            <Mark
              x={CB1}
              y={1787}
              blue
              on={glassVal === EarlyScrapGlassResult.SIMPLE_DAMAGE}
            />

            {/* 타이어 */}
            <Mark
              x={CB1}
              y={1831}
              blue
              on={tireVal === EarlyScrapTireResult.GOOD}
            />
            <Mark
              x={CB2}
              y={1831}
              blue
              on={tireVal === EarlyScrapTireResult.BAD}
            />
            <Mark
              x={CB1}
              y={1870}
              blue
              on={tireVal === EarlyScrapTireResult.SIMPLE_BAD_AIR}
            />

            {/* 등화장치 */}
            <Mark
              x={CB1}
              y={1915}
              blue
              on={lightingVal === EarlyScrapLightingResult.INSTALLED}
            />
            <Mark
              x={CB2}
              y={1915}
              blue
              on={lightingVal === EarlyScrapLightingResult.NOT_INSTALLED}
            />
            <Mark
              x={CB1}
              y={1953}
              blue
              on={lightingVal === EarlyScrapLightingResult.SIMPLE_DAMAGE}
            />

            {/* 첨부물 */}
            <Mark x={570} y={2007} on={isOnline} />
            <Mark x={570} y={2055} on={isOnsite} />

            {/* 판정 */}
            <Mark
              x={836}
              y={2119}
              blue
              on={
                judgment.operationalStatus ===
                EarlyScrapOperationalJudgment.PASS
              }
            />
            <Mark
              x={1085}
              y={2119}
              blue
              on={
                judgment.operationalStatus ===
                EarlyScrapOperationalJudgment.FAIL
              }
            />
            <Field x={797} y={2172}>
              {v(judgment.failReason)}
            </Field>
            <Field x={1437} y={2172}>
              {v(judgment.overallOpinion)}
            </Field>

            {/* 년 / 월 / 일 / 점검장 — 칸 밖, 여백 없음 */}
            <FieldPlain x={1310} y={2438} className="es-ov-date">
              {y}
            </FieldPlain>
            <FieldPlain x={1526} y={2438} className="es-ov-date">
              {m}
            </FieldPlain>
            <FieldPlain x={1670} y={2438} className="es-ov-date">
              {day}
            </FieldPlain>
            <FieldPlain x={1424} y={2482} className="es-ov-sign">
              {v(inspector.shopName)}
            </FieldPlain>
            {associationSealUrl ? (
              <img
                className="es-ov-stamp"
                src={associationSealUrl}
                alt="기술인협회 직인"
                style={styleXY(1610, 2468)}
              />
            ) : null}
          </div>
        </div>
      </section>

      {isOnsite && (
        <section className="es-print-photos">
          <div className="es-print-badge">[{EARLY_SCRAP_FORM_META.formType}]</div>
          <h2 className="es-photo-title">대상차량 확인 사진</h2>
          <p className="es-photo-meta">
            확인번호 {v(formValues.inspectNumber)}
            {vehicle.licensePlateNumber
              ? ` / ${vehicle.licensePlateNumber}`
              : ''}
            {' / 현장 확인 : 사진 4매(전·후·좌·우면)'}
          </p>
          <div className="es-photo-grid">
            {EARLY_SCRAP_PHOTO_BLUEPRINT.positions.map(
              ({ position, htmlLabel }) => (
                <div key={position} className="es-photo-card">
                  <div className="es-photo-label">{htmlLabel}</div>
                  <div className="es-photo-frame">
                    {photoPreviews[position] ? (
                      <img src={photoPreviews[position]} alt={htmlLabel} />
                    ) : null}
                  </div>
                </div>
              )
            )}
          </div>
          <div className="es-page-no">- 2 -</div>
        </section>
      )}
    </div>
  );
};

export default EarlyScrapPrintDocument;
